import { Link, usePage } from '@inertiajs/react'
import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react'

import Layout from '../../components/Layout'
import { useTranslation } from '@/lib/i18n'
import { useCurrency } from '@/lib/currency'
import { useIsMini } from '@/lib/useMediaQuery'
import { splitName } from '@/lib/productName'
import { EmptyState } from '@/components/ui/empty-state'
import CardMeta from '@/components/CardMeta'
import { BagIcon } from '@/components/Icons'
import type { SharedProps } from '@/types'
import { ProductDrop, phoneProductDropFor } from '@/components/ProductDrop'
import { StoreImage } from '@/components/StoreImage'
import SelectedVideo from '@/components/SelectedVideo'
import heroMedia from '@/lib/heroMedia.json'

interface Product {
  id: number
  name: string
  /** Category-scoped original media stem, independent of the display name. */
  assetSlug: string
  description: string
  price: number
  /** Final tags by currency code; USD always, RUB / BYN when set. */
  prices?: Partial<Record<string, number>>
  stock: number
  inStock: boolean
  tag: string | null
  material: string | null
  imageUrl: string | null
  thumbnailUrl?: string | null
  hoverImageUrl: string | null
  /** Card-front render under public/card-fronts, or null for the cover photo. */
  cardUrl: string | null
  customizable: boolean
  customizeUrl: string | null
  oneOfOne: boolean
  /** A made-to-order slot. It leaves the grid and becomes its line's
   *  custom bar instead of standing among the stock. */
  custom: boolean
  /** One of Product::CATEGORIES, or null for a piece off the landing rows. */
  category: string | null
  /** Reveal clip under public/product-videos — the phone's hero
   *  plays it in place of the cover shot. */
  videoUrl: string | null
  /** Transparent render under public/cutouts — what the desktop strip
   *  floats inside its glass card. Null means the piece shows its
   *  cover photo instead. */
  cutoutUrl: string | null
  /** Renders behind a made-to-order slot's bar: its line's pieces. */
  fan?: string[]
}

interface Props {
  products: Product[]
}

/** The order the sheets walk the lines in. */
const CATEGORIES = ['apple', 'airpods_pro_cases', 'vape_cases', 'lighter_cases'] as const

/** The line a piece belongs to, or the catch-all row it falls into. */
function categoryKey(product: Product): string {
  return CATEGORIES.includes(product.category as (typeof CATEGORIES)[number])
    ? (product.category as string)
    : 'more'
}

/** Header links jump directly to the catalog row for their line. */
function categoryAnchor(key: string): string {
  return `shop-${key}`
}

/** Each main product line gets a phone-hero shortcut, with the stand first. */
const PHONE_LEAD = heroMedia.phoneLead

function phoneLeadPieces(products: Product[]): Product[] {
  const picked = PHONE_LEAD.map((slug) =>
    products.find((product) => product.assetSlug === slug)
  ).filter((product): product is Product => Boolean(product))

  return picked.length > 0 ? picked : products
}

/** Preserve the original full-screen photo framing, including its studio backdrop. */
const LEAD_FRAMING: Record<
  string,
  { piece: { top: number; bottom: number; width: number }; scrim: string }
> = {
  'f-ck-stand': {
    piece: { top: 0.1, bottom: 0.875, width: 0.6 },
    scrim:
      'bg-[linear-gradient(180deg,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0)_22%,rgba(0,0,0,0)_66%,rgba(0,0,0,0.8)_84%,rgba(0,0,0,0.94)_100%)]',
  },
  'max-charging-stand': {
    piece: { top: 0.1, bottom: 0.875, width: 0.6 },
    scrim:
      'bg-[linear-gradient(180deg,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0)_22%,rgba(0,0,0,0)_66%,rgba(0,0,0,0.8)_84%,rgba(0,0,0,0.94)_100%)]',
  },
}

const LEAD_SCRIM =
  'bg-[linear-gradient(180deg,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0)_25%,rgba(0,0,0,0)_38%,rgba(0,0,0,0.78)_78%,rgba(0,0,0,0.94)_100%)]'

const LEAD_SHRINK: Record<string, number> = { 'crystal-gem-sick-airpods-pro-case': 0.6, 'sick-cricket-lighter': 0.6, 'piercings-vape-case': 0.8, 'i-hate-everyone-cricket-lighter': 0.85 }

/** Lead slides with their own localized category call to action. */
const LEAD_CTA: Record<string, string> = {
  apple: 'home.lead.max_stand',
  airpods_pro_cases: 'home.lead.airpods_skins',
  vape_cases: 'home.lead.vape_skin',
  lighter_cases: 'home.lead.cricket_skin',
}

/** Fit the complete stand above the thumb rail using the original square-photo measurements. */
function framedStyle({ top, bottom, width }: { top: number; bottom: number; width: number }): CSSProperties {
  const span = (bottom - top).toFixed(3)
  const fit = `${(97 / width).toFixed(1)}vw`
  const zoom = `${(135 / width).toFixed(1)}vw`
  const tall = `calc((var(--sss-phone-hero-height) - 230px) / ${span})`
  const roomy = `calc((var(--sss-phone-hero-height) - 360px) / ${span})`
  const side = `min(${tall}, max(${fit}, min(${roomy}, ${zoom})))`
  const spare = `max(0px, (var(--sss-phone-hero-height) - 360px - ${side} * ${span}) * 0.5)`
  return {
    width: side,
    height: side,
    left: '50%',
    top: `calc(var(--sss-phone-hero-height) - 170px - ${side} * ${bottom} - ${spare})`,
    transform: 'translateX(-50%)',
    maskImage: 'linear-gradient(180deg, transparent 0%, #000 9%, #000 90%, transparent 100%)',
    WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 9%, #000 90%, transparent 100%)',
  }
}

/**
 * The pieces that open the wide hero's row, in this order; every other
 * piece follows in the catalog's own order. Same matching as the
 * phone's lead.
 */
const STRIP_FIRST = heroMedia.stripFirst

function stripOrder(products: Product[]): Product[] {
  const rank = (product: Product) => {
    const index = STRIP_FIRST.indexOf(product.assetSlug)
    return index === -1 ? STRIP_FIRST.length : index
  }
  // Array#sort is stable, so the pieces that tie keep their order.
  return [...products].sort((a, b) => rank(a) - rank(b))
}

/** The gutter both the wide hero's rows start from. */
const GUTTER = 'px-[clamp(24px,4.5vw,64px)]'

/** Mobile showroom and desktop strip share the ticker and catalog below. */
export default function ProductsIndex({ products }: Props) {
  const { t } = useTranslation()
  // Only the hero for the current width is mounted. CSS-hiding the other
  // still paid to mount and decode its imagery.
  const isMini = useIsMini()

  if (products.length === 0) {
    return (
      <Layout>
        <section className="sss-page mt-10">
          <EmptyState icon={<BagIcon size={28} />} title={t('home.empty_msg')} />
        </section>
      </Layout>
    )
  }

  return (
    <Layout overlayHeader stripNav transparentHeader announcement={<HomeTicker />}>
      {isMini ? <PhoneLead products={phoneLeadPieces(products)} /> : <DesktopHero products={products} />}
      <Catalog products={products} />
    </Layout>
  )
}

/* ------------------------------------------------------------------ */
/* The lead — one per width                                            */
/* ------------------------------------------------------------------ */

/** Fill the visible phone viewport through the CTA, then continue directly into the catalog. */
function PhoneLead({ products }: { products: Product[] }) {
  const { t } = useTranslation()
  const isMini = useIsMini()
  const [slide, setSlide] = useState(0)
  const [playedDrops, setPlayedDrops] = useState<Set<number>>(() => new Set())

  const current = products[slide]
  if (!current) return null
  const key = categoryKey(current)
  const line = key === 'apple'
    ? t('home.apple_accessories')
    : key === 'more' ? null : t(`home.category.${key}`)
  const categoryLabel = line ? t('home.shop_category', { category: line }) : t('home.shop_now')
  const ownCta = LEAD_CTA[key]
  const shopLabel = ownCta ? t(ownCta) : categoryLabel

  return (
    <section id="phone-showroom" className="relative [--sss-phone-hero-height:calc(100dvh-44px)] h-[var(--sss-phone-hero-height)] overflow-hidden bg-black text-white min-[560px]:hidden">
      <div className="absolute inset-0">
        {products.map((product, index) => {
          if (index !== slide) return null
          const nickname = product.assetSlug
          const framing = LEAD_FRAMING[nickname]
          const shrink = LEAD_SHRINK[nickname]
          // Stand and AirPods leads use front photos; their gallery clips stay on the PDP.
          const isStand = product.category === 'apple' || Boolean(framing)
          const isAirpods = product.category === 'airpods_pro_cases'
          const frontImage = (isAirpods && product.cardUrl) || product.imageUrl
          const drop = isMini && !isStand && !isAirpods ? phoneProductDropFor(product.cutoutUrl) : null
          return drop ? (
            <div
              key={product.id}
              aria-hidden={index !== slide}
              className="absolute left-1/2 top-0 aspect-square max-w-none -translate-x-1/2 transition-opacity duration-500 ease-standard motion-reduce:transition-none"
              style={{
                height: `${(shrink ?? 1) * 100}%`,
                top: nickname === 'sick-cricket-lighter' && shrink ? '8%' : undefined,
                opacity: index === slide ? 1 : 0,
                maskImage: 'linear-gradient(180deg, #000 88%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(180deg, #000 88%, transparent 100%)',
              }}
            >
              {product.imageUrl && <StoreImage src={product.imageUrl} alt="" className="absolute inset-0 size-full object-cover" />}
              <ProductDrop scene={drop} alt={product.name} active={index === slide} static={playedDrops.has(product.id)} onSettled={() => setPlayedDrops(previous => new Set(previous).add(product.id))} className="relative transition-none data-[state=loading]:opacity-0 data-[state=failed]:opacity-0" />
            </div>
          ) : product.videoUrl && isMini && !isStand && !isAirpods ? (
          <SelectedVideo
            key={product.id}
            src={product.videoUrl}
            poster={product.imageUrl || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden={index !== slide}
            className="absolute inset-0 size-full object-cover transition-opacity duration-500 ease-standard motion-reduce:transition-none"
            style={{ opacity: index === slide ? 1 : 0 }}
          />
        ) : frontImage ? (
          <StoreImage
            key={product.id}
            src={frontImage}
            fetchPriority="high"
            loading="eager"
            alt={product.name}
            aria-hidden={index !== slide}
            className={`transition-opacity duration-500 ease-standard motion-reduce:transition-none ${
              framing
                ? 'absolute max-w-none object-cover'
                : shrink
                  ? 'absolute left-1/2 top-1/2 w-auto max-w-none -translate-x-1/2 -translate-y-1/2 object-cover'
                  : 'absolute inset-0 size-full object-cover'
            }`}
            style={{
              ...(framing ? framedStyle(framing.piece) : shrink ? {
                height: `${shrink * 100}%`,
                maskImage: 'linear-gradient(180deg, #000 88%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(180deg, #000 88%, transparent 100%)',
              } : {}),
              opacity: index === slide ? 1 : 0,
            }}
          />
        ) : null})}
      </div>
      <div aria-hidden="true" className={`pointer-events-none absolute inset-0 ${LEAD_FRAMING[current.assetSlug]?.scrim ?? LEAD_SCRIM}`} />

      <div className="absolute inset-x-5 bottom-6 z-[5] flex flex-col items-center gap-4">
        {products.length > 1 && (
          <div className="sss-rail flex max-w-full gap-2 overflow-x-auto pb-0.5">
            {products.map((product, index) => (
              <button
                key={product.id}
                type="button"
                aria-label={product.name}
                aria-current={index === slide}
                onClick={() => setSlide(index)}
                className={`h-16 w-13 flex-none cursor-pointer overflow-hidden rounded-[10px] border-2 bg-surface transition-opacity duration-200 ease-standard ${
                  index === slide ? 'border-white opacity-100' : 'border-hairline opacity-70'
                }`}
              >
                {product.thumbnailUrl && (
                  <StoreImage src={product.thumbnailUrl} alt="" width={52} height={64} className="size-full object-cover" />
                )}
              </button>
            ))}
          </div>
        )}

        <a
          href={`#${categoryAnchor(key)}`}
          className="flex min-h-[56px] w-full items-center justify-center rounded-2xl bg-white px-5 text-center text-[clamp(13px,3.6vw,15px)] font-bold uppercase leading-tight tracking-[0.04em] text-[#0A0A0A] hover:text-[#0A0A0A]"
        >
          {shopLabel}
        </a>
      </div>
    </section>
  )
}

/**
 * 1c · Strip. The headline holds the top-left, the shop line sits at
 * its baseline on the right, and the pieces run along the foot in a row
 * that deliberately overflows the right edge — the sheet's way of
 * saying there is more than fits.
 *
 * The cards are the product page's frosted glass, which means the
 * section has to carry the `sss-glass-band` variables and give the blur
 * something to work on: CSS ambient lamps without downloading artwork.
 */
function DesktopHero({ products }: { products: Product[] }) {
  const { t } = useTranslation()

  // Eight is already twice what a 1440 window shows; past that the row
  // is only costing fetches nobody scrolls to.
  const cards = stripOrder(products)
    .filter((product) => product.cutoutUrl || product.imageUrl)
    .slice(0, 8)
  if (cards.length === 0) return null

  const lamps = cards.filter((product) => product.cutoutUrl)

  return (
    <section
      id="desktop-showroom"
      className={
        // Sized by what is in it, not by the window. Stretching the hero
        // to the fold and spacing its two rows apart only opened a hole
        // between the headline and the strip — on a 900px window it was
        // 200px of nothing. The strip now follows the headline at one
        // fixed gap whatever the monitor, and the catalog starts
        // wherever that leaves it.
        'sss-glass-band relative hidden flex-col overflow-hidden ' +
        // The sheet's own frame: 56 over the headline, 40 under the row.
        'pb-10 pt-14 min-[560px]:flex'
      }
    >
      {lamps.length > 0 && <HeroGlow />}

      {/* The headline alone. The shop line and the "all pieces" button that
          used to sit at its baseline are gone: the strip under them is the
          way into the catalog, and the catalog itself is the next screen. */}
      <div className={`relative z-[2] ${GUTTER}`}>
        <h1 className="sss-display m-0 max-w-[820px] text-[length:var(--sss-size-hero)]">
          {t('home.hero_headline')}
        </h1>
      </div>

      {/* Four across, and the fifth runs off the right edge by design —
          the sheet's way of saying there is more than fits. It scrolls
          rather than wraps, so the pieces past the fold are still
          reachable without the row ever becoming a grid. */}
      <div className={`sss-rail relative z-[2] flex gap-4 overflow-x-auto pb-2 pt-11 ${GUTTER}`}>
        {cards.map((product, index) => (
          <StripCard key={product.id} product={product} priority={index === 0} />
        ))}
      </div>
    </section>
  )
}

/**
 * The glass card off the product page's "choose your piece": the name
 * in the display face on top, the render floating under it, and the
 * sheet's own line — what the piece is, and its price — under both.
 *
 * Four fill the row exactly, whatever the window: the width is a share
 * of the rail's own content box (three 16px gaps taken out first), not
 * of the viewport, so a scrollbar can't push the fourth card out. Below
 * roughly 1000px that share would leave the render no room, so the card
 * stops at 200px and the row starts scrolling instead.
 */
function StripCard({ product, priority = false }: { product: Product; priority?: boolean }) {
  return (
    <div className="sss-glass-card group relative flex w-[max(200px,calc((100%-48px)/4))] flex-none flex-col gap-3.5 rounded-[18px] px-4.5 pb-4.5 pt-6">
      <Link
        href={`/products/${product.id}`}
        className="sss-graffiti relative px-1 text-center text-[26px] leading-[0.95] text-ink hover:text-ink"
      >
        {splitName(product.name).title}
      </Link>
      <Link
        href={`/products/${product.id}`}
        aria-label={product.name}
        className="relative flex h-[240px] items-center justify-center"
      >
        {product.cutoutUrl ? (
          <StoreImage
            src={product.cutoutUrl}
            alt={product.name}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            className="max-h-full max-w-[82%] object-contain [filter:var(--gb-cast)] transition-transform duration-[350ms] ease-standard group-hover:-rotate-2 group-hover:scale-105"
          />
        ) : (
          // No transparent render: the cover shot takes the same box as
          // a photo inside the glass, so the row stays one rhythm.
          <StoreImage
            src={product.imageUrl as string}
            alt={product.name}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            className="size-full rounded-[14px] object-cover transition-transform duration-[350ms] ease-standard group-hover:scale-105"
          />
        )}
      </Link>
      <CardMeta
        category={product.category}
        material={product.material}
        price={product.price}
        prices={product.prices}
      />
    </div>
  )
}

/** CSS lamps retain the glass haze without downloading detailed, invisible artwork. */
function HeroGlow() {
  const spots: {
    inset: React.CSSProperties
    width: number
    blur: number
    /** The sheet lights the first lamp a step brighter than the pair. */
    lead?: boolean
  }[] = [
    { inset: { left: '4%', bottom: '2%' }, width: 300, blur: 76, lead: true },
    { inset: { left: '44%', bottom: '-10%' }, width: 330, blur: 84 },
    { inset: { right: '8%', bottom: '6%' }, width: 290, blur: 76 },
  ]

  return (
    <>
      {spots.map((spot, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="pointer-events-none absolute z-[1] aspect-square max-w-none bg-[radial-gradient(ellipse,var(--sss-accent)_0%,transparent_70%)]"
          style={{
            ...spot.inset,
            width: spot.width,
            filter: `blur(${spot.blur}px) saturate(var(--gb-glow-sat))`,
            opacity: `calc(var(${spot.lead ? '--gb-glow-lead' : '--gb-glow'}) * 0.5)`,
          }}
        />
      ))}
    </>
  )
}

/** The top ticker carries the workshop, gift and personalization messages. */
function HomeTicker() {
  const { t } = useTranslation()
  const { cart } = usePage<SharedProps>().props
  const gift = cart.gift
  const frame = useRef<HTMLDivElement>(null)
  const sequence = useRef<HTMLDivElement>(null)
  const [layout, setLayout] = useState({ repeats: 1, duration: 39.216 })

  useLayoutEffect(() => {
    const measure = () => {
      if (!frame.current || !sequence.current) return
      const width = sequence.current.getBoundingClientRect().width
      if (width <= 0) return
      // Every half must cover the viewport, even at the very end of the loop.
      // Measure one natural sequence, not the expanded track (which feeds back).
      const repeats = Math.max(1, Math.ceil(frame.current.clientWidth / width))
      const duration = width * repeats / 26.4
      setLayout(previous => previous.repeats === repeats && previous.duration === duration
        ? previous : { repeats, duration })
    }
    measure()
    const observer = new ResizeObserver(measure)
    if (frame.current) observer.observe(frame.current)
    if (sequence.current) observer.observe(sequence.current)
    return () => observer.disconnect()
  }, [])

  // The original 480px artwork remains for product pages; this 64px version
  // avoids decoding a 390KB PNG just to display a 32px separator.
  const separatorUrl = gift?.cutoutUrl === '/cutouts/sick-graffiti-tag-can-opener.png'
    ? '/icons/ticker-can-opener.webp' : gift?.cutoutUrl

  const separator = separatorUrl ? (
    <StoreImage
      src={separatorUrl}
      alt=""
      aria-hidden="true"
      width={32}
      height={32}
      decoding="async"
      className="size-8 shrink-0 object-contain"
    />
  ) : null

  return (
    <div ref={frame} id="home-ticker" className="flex h-11 items-center overflow-hidden border-y border-hairline bg-white text-sm font-semibold uppercase text-black [contain:paint] motion-reduce:overflow-x-auto">
      <div className="sss-marquee sss-home-ticker flex w-max shrink-0 will-change-transform motion-reduce:animate-none motion-reduce:will-change-auto" style={{ animationDuration: `${layout.duration}s` }}>
        {[0, 1].map((copy) => (
          <div key={copy} aria-hidden={copy === 1 || undefined} className={`flex shrink-0 ${copy === 1 ? 'motion-reduce:hidden' : ''}`}>
            {Array.from({ length: layout.repeats }, (_, repeat) => (
              <div key={repeat} ref={copy === 0 && repeat === 0 ? sequence : undefined} aria-hidden={repeat > 0 || undefined} className={`flex shrink-0 items-center whitespace-nowrap ${repeat > 0 ? 'motion-reduce:hidden' : ''}`}>
                <span className="px-6">{t('home.ticker.unique')}</span>
                {separator}
                {gift && <>
                  <Link href={gift.href} tabIndex={copy > 0 || repeat > 0 ? -1 : undefined} className="px-6 hover:text-black hover:underline">
                    {t('home.ticker.gift')}
                  </Link>
                  {separator}
                </>}
                <span className="px-6">{t('home.ticker.custom')}</span>
                {separator}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* The catalog, by line — shared by both widths                        */
/* ------------------------------------------------------------------ */

function Catalog({ products }: { products: Product[] }) {
  const { t } = useTranslation()

  // The made-to-order slot of a line is not one of its tiles: it is the
  // bar under them, so the row reads "here is the stock, or have one
  // built" rather than putting an empty slot among finished pieces.
  const stock = products.filter((product) => !product.custom)
  const customFor = (key: string) =>
    products.find((product) => product.custom && product.category === key) ?? null

  const rows: { key: string; title: string; items: Product[]; custom: Product | null }[] =
    CATEGORIES.map((key) => ({
      key,
      title: t(`home.category.${key}`),
      items: stock.filter((product) => product.category === key),
      custom: customFor(key),
    }))

  // A piece nobody gave a line to still has to be reachable, so it takes
  // the last row rather than falling off the page.
  const loose = stock.filter((product) => categoryKey(product) === 'more')
  if (loose.length > 0) {
    rows.push({ key: 'more', title: t('home.category.more'), items: loose, custom: null })
  }

  return (
    <section
      id="shop"
      className="mt-3 flex w-full flex-col gap-10 px-3.5 pb-10 min-[560px]:mx-auto min-[560px]:mt-0 min-[560px]:max-w-[1280px] min-[560px]:gap-[72px] min-[560px]:px-[clamp(24px,5vw,64px)] min-[560px]:py-24"
    >
      {rows.map((row) => (
        <CategoryRow
          key={row.key}
          id={categoryAnchor(row.key)}
          title={row.title}
          items={row.items}
          custom={row.custom}
        />
      ))}
    </section>
  )
}

/**
 * One line of the catalog: a display-face heading with the count
 * opposite it over a hairline, then the pieces — two up on a phone, as
 * many as fit above it. A line with nothing in it yet is drawn as its
 * own teaser, striped tiles under a "coming soon" count, rather than
 * left out.
 */
function CategoryRow({
  id,
  title,
  items,
  custom,
}: {
  id: string
  title: string
  items: Product[]
  custom: Product | null
}) {
  const { t } = useTranslation()
  const soon = items.length === 0

  return (
    // The offset keeps the heading clear of the floating mark when a
    // lead's shop button jumps straight onto the row.
    <div id={id} className="flex scroll-mt-[82px] flex-col gap-3.5 min-[560px]:gap-[22px]">
      <div className="flex items-baseline justify-between gap-3 border-b border-hairline pb-2.5 min-[560px]:gap-4 min-[560px]:pb-3.5">
        <h2 className="sss-display text-[30px] font-normal min-[560px]:text-[clamp(36px,4.6vw,60px)]">
          {title}
        </h2>
        <span className="whitespace-nowrap text-[13px] text-ink-faint min-[560px]:text-[14px]">
          {soon
            ? t('home.coming_soon')
            : t('home.pieces', { count: String(items.length).padStart(2, '0') })}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2.5 min-[560px]:grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))] min-[560px]:gap-4">
        {soon
          ? [1, 2, 3].map((n) => (
              <SoonTile key={n} name={t('home.category.placeholder', { n: `00${n}` })} />
            ))
          : items.map((product) => <Tile key={product.id} product={product} />)}
      </div>

      {custom && <CustomBar product={custom} />}
    </div>
  )
}

/**
 * The row's custom offer, using the glass sheet's 1a frost over pink ambient light.
 *
 * Every line ends with the same offer: the stock above, or have one
 * built. The bar is the line's made-to-order slot, lifted out of the
 * grid so it reads as an offer rather than a piece with nothing in it.
 *
 * Mobile places the title and artwork above a full-width order action;
 * desktop arranges the artwork, title and action in one row.
 *
 * The title, artwork and order action carry the offer without repeating
 * its product type and payment terms. The existing 1a glass roles keep
 * the frost, edge and text readable in both themes.
 */
function CustomBar({ product }: { product: Product }) {
  const { t } = useTranslation()
  const { format: formatPrice } = useCurrency()

  // Stands stay level and AirPods use one larger design; only skins fan out.
  const singleArtwork = product.category === 'apple' || product.category === 'airpods_pro_cases'
  const isAirpods = product.category === 'airpods_pro_cases'
  const fan = (product.fan?.length ? product.fan : [product.cutoutUrl || product.imageUrl]).filter(
    (url): url is string => Boolean(url)
  ).slice(0, singleArtwork ? 1 : 3)

  // Front, left-behind, right-behind: offsets and tilts per depth, the
  // phone's fan tighter because it hangs out of a corner.
  const FAN = [
    { phone: '-rotate-[4deg] z-[3]', wide: 'min-[920px]:-rotate-[6deg] min-[920px]:z-[3]' },
    {
      phone: '-translate-x-9 -rotate-[22deg] z-[2]',
      wide: 'min-[920px]:-translate-x-14 min-[920px]:-rotate-[20deg] min-[920px]:z-[2]',
    },
    {
      phone: 'translate-x-8 -translate-y-1 rotate-[14deg] z-[1]',
      wide: 'min-[920px]:translate-x-14 min-[920px]:-translate-y-1 min-[920px]:rotate-[12deg] min-[920px]:z-[1]',
    },
  ]

  return (
    <div data-custom-card={product.category} className="sss-glass-band relative mt-1 isolate overflow-hidden rounded-[18px] bg-[color:var(--gb-panel)] min-[560px]:mt-0">
      {/* Ambient lamps behind the glass, like "choose your piece", in pink. */}
      <span aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-1/4 h-60 w-4/5 rounded-full bg-signal/70 blur-3xl" />
      <span aria-hidden="true" className="pointer-events-none absolute -right-10 -top-20 h-48 w-56 rounded-full bg-signal/30 blur-3xl" />
      <Link
        href={`/products/${product.id}`}
        aria-label={product.name}
        className={
          'sss-glass-card sss-glass-card--static group relative grid grid-cols-1 gap-3.5 overflow-hidden rounded-[18px] ' +
          'px-4.5 pb-4.5 pt-5 text-ink hover:text-ink ' +
          'min-[920px]:grid-cols-[200px_minmax(0,1fr)_auto] ' +
          'min-[920px]:items-center min-[920px]:gap-6 min-[920px]:py-[18px] min-[920px]:pl-5 ' +
          'min-[920px]:pr-7'
        }
      >
        {fan.length > 0 && (
          <span
            className={
              'pointer-events-none absolute top-4 grid h-32 place-items-center ' +
              (isAirpods ? 'right-4 w-36 ' : singleArtwork ? 'right-6 w-24 ' : 'right-12 w-24 ') +
              'min-[920px]:static min-[920px]:h-[150px] min-[920px]:w-full'
            }
          >
            {/* Keep every render capped on both axes so tall skins clear the CTA. */}
            {fan.map((url, i) => (
              <StoreImage
                key={url}
                src={url}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className={
                  'col-start-1 row-start-1 object-contain ' +
                  (isAirpods ? 'max-h-32 max-w-36 ' : singleArtwork ? 'max-h-28 max-w-24 ' : 'max-h-24 max-w-20 ') +
                  '[filter:drop-shadow(0_12px_16px_rgba(0,0,0,.35))] transition-transform ' +
                  'duration-[350ms] ease-standard min-[920px]:max-h-[150px] min-[920px]:max-w-[120px] ' +
                  'min-[920px]:[filter:drop-shadow(0_16px_20px_rgba(0,0,0,.35))] ' +
                  (singleArtwork ? '' : `${FAN[i].phone} ${FAN[i].wide}`)
                }
              />
            ))}
          </span>
        )}

        <span className="flex min-h-[104px] min-w-0 flex-col pr-32 pt-3 min-[920px]:min-h-0 min-[920px]:pr-0 min-[920px]:pt-0">
          <span className="block text-[clamp(24px,7vw,32px)] font-semibold uppercase leading-tight text-ink min-[560px]:text-[38px]">
            {t('home.custom.or')} {t('home.custom.title')}
          </span>
        </span>

        <span
          className={
            'flex min-h-[52px] items-center justify-between rounded-xl bg-[#0A0A0A] px-4.5 ' +
            'text-[14px] font-bold uppercase text-white min-[920px]:h-[52px] min-[920px]:justify-start ' +
            'min-[920px]:gap-3.5 min-[920px]:whitespace-nowrap min-[920px]:px-[22px]'
          }
        >
          {t('home.custom.cta')}
          <span className="tabular-nums text-signal">
            {t('home.custom.from', { price: formatPrice(product.price, product.prices) })}
          </span>
        </span>
      </Link>
    </div>
  )
}

function Tile({ product }: { product: Product }) {
  const { format: formatPrice } = useCurrency()
  // The dedicated cover normalizes the physical case size without changing the gallery photos.
  // Static public files are cached for a year; invalidate the replaced cover photos.
  const imageUrl = product.cardUrl ? `${product.cardUrl}?v=20261016-crystal-size` : product.imageUrl
  // AirPods cover renders are already normalized to the same case scale.
  const preserveProductScale = product.category === 'airpods_pro_cases'

  return (
    <Link href={`/products/${product.id}`} className="group block min-w-0 text-ink hover:text-ink">
      <div className="relative aspect-[4/5] overflow-hidden rounded-[18px] border border-hairline bg-surface">
        {imageUrl && (
          <StoreImage
            src={imageUrl}
            alt={product.name}
            loading="lazy"
            className={`absolute inset-0 size-full object-cover transition-transform duration-500 ease-standard ${preserveProductScale ? '' : product.cardUrl ? 'scale-[1.35]' : 'group-hover:scale-[1.04]'}`}
          />
        )}
      </div>
      <div className="flex justify-between gap-2 px-0.5 pt-2.5 min-[560px]:pt-3">
        <span className="text-[14px] font-semibold leading-tight min-[560px]:text-[15px]">
          {splitName(product.name).title}
        </span>
        <span className="shrink-0 text-[13px] tabular-nums text-ink-faint min-[560px]:text-[14px]">
          {formatPrice(product.price, product.prices)}
        </span>
      </div>
    </Link>
  )
}

/** A line that has been announced but has nothing to sell yet. */
function SoonTile({ name }: { name: string }) {
  const { t } = useTranslation()

  return (
    <div className="block min-w-0">
      <div className="sss-soon-tile relative aspect-[4/5] overflow-hidden rounded-[18px] border border-hairline">
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="rounded-full border border-hairline bg-background px-3 py-1.5 text-[12px] text-ink-faint">
            {t('home.coming_soon')}
          </span>
        </div>
      </div>
      <div className="flex justify-between gap-2 px-0.5 pt-2.5 min-[560px]:pt-3">
        <span className="text-[14px] font-semibold leading-tight text-ink-faint min-[560px]:text-[15px]">
          {name}
        </span>
        <span className="shrink-0 text-[13px] tabular-nums text-ink-faint min-[560px]:text-[14px]">
          —
        </span>
      </div>
    </div>
  )
}
